import * as React from 'react'
import styled from 'styled-components'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import NoPrint from 'components/utils/NoPrint'
import { CurrencyProvider } from 'contexts/currencyContext'
import type { IntlShape } from 'react-intl'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import { getOfferValueDisplayVisibility } from 'lib/offer/getOfferDisplayPricing'

export type DiscountDisplayType = 'badge' | 'inlineBadge' | 'following' | 'none'

export type Props = {
  depositComponent?: React.ReactNode
  discountDisplayType?: DiscountDisplayType
  discount?: number
  price: number
  luxPlusPrice?: number
  value?: number
  currencyCode: string
  priceLabel: string
  totalLabel?: string
  roomOccupancy?: string
  className?: string
  showTaxesAndFees?: boolean
  offerConfig?: App.OfferLuxPlusConfig
  showValueWithoutDiscount?: boolean
}

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const RoomText = styled.div`
  font-size: 11px;
  font-weight: ${(props) => props.theme.font.primary.weight.regular};
  line-height: 16px;
  color: ${(props) => props.theme.palette.neutral.surface.lightForegroundAlt};
`

export function buildNightsPriceLabel(intl: IntlShape, min: number, max: number) {
  if (min !== max) {
    return intl.formatMessage({
      id: 'nAbRIf4beX_JNuhSni4M',
      defaultMessage: '{min} to {max} nights from',
      description: 'Price label for a range of nights, e.g. "3 to 5 nights from"',
    }, { min, max })
  }
  return intl.formatMessage({
    id: '6KK0bmzirHb-25sBNoMP',
    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
    description: 'Price label for a number of nights, e.g. "5 nights from"',
  }, { count: min })
}

export function buildDaysPriceLabel(intl: IntlShape, min: number, max: number) {
  if (min !== max) {
    return intl.formatMessage({
      id: 'PIeI7aG5FlEBEJDdqjXq',
      defaultMessage: '{min} to {max} days from',
      description: 'Price label for a range of days, e.g. "3 to 5 days from"',
    }, { min, max })
  }
  return intl.formatMessage({
    id: 'e6k1Q8InWKnFJThXgCvR',
    defaultMessage: '{count, plural, one {# day} other {# days}} from',
    description: 'Price label for a number of days, e.g. "5 days from"',
  }, { count: min })
}

function PriceDetails({
  depositComponent,
  discountDisplayType = 'following',
  discount,
  price,
  luxPlusPrice,
  value,
  currencyCode,
  priceLabel,
  totalLabel,
  roomOccupancy,
  className,
  showTaxesAndFees = true,
  offerConfig,
  showValueWithoutDiscount = false,
}: Props) {
  const displayedLuxPlusMode = useLuxPlusDisplayedMode({
    price,
    memberPrice: luxPlusPrice ?? 0,
    offerConfig,
  })
  const showInlineDiscount = displayedLuxPlusMode === 'regular-only' && discountDisplayType === 'inlineBadge'

  const { shouldShowDiscount } = getOfferValueDisplayVisibility({ discountPercent: discount })
  const displayDiscount = shouldShowDiscount ? discount : undefined
  const hasDepositData = (discountDisplayType === 'badge' && !!displayDiscount) || !!depositComponent

  const depositData = (
    <>
      {discountDisplayType === 'badge' && displayDiscount && (
        <NoPrint>
          <PriceRowValueDiscount
            type="estimate"
            discountPercentage={displayDiscount}
          />
        </NoPrint>
      )}
      {depositComponent && <span>{depositComponent}</span>}
    </>
  )

  return (
    <CurrencyProvider currency={currencyCode}>
      <PriceWrapper className={className}>
        <VerticalSpacer gap={4}>
          {hasDepositData && (
            <VerticalSpacer gap={8}>
              {depositData}
            </VerticalSpacer>
          )}
          <PriceRowPriceCaption>{priceLabel}</PriceRowPriceCaption>
        </VerticalSpacer>
        {showInlineDiscount && (
          <PriceRowPrice
            size="M"
            price={price}
            saleUnit={totalLabel ?? 'total'}
            discountPercentage={
              discountDisplayType === 'inlineBadge' ? displayDiscount : undefined
            }
            discountType="estimate"
          />
        )}
        {!showInlineDiscount && (
          <LuxPlusPriceStack
            price={price}
            size={displayedLuxPlusMode === 'regular-only' ? 'M' : 'L'}
            saleUnit={totalLabel ?? 'total'}
            memberPrice={luxPlusPrice}
            offerConfig={offerConfig}
            mode={displayedLuxPlusMode}
          />
        )}
        {discountDisplayType === 'following' && !!value && (!!discount || showValueWithoutDiscount) && (
          <NoPrint>
            <PriceRowValueDiscountWithCaption
              size="L"
              originalValue={Math.ceil(value)}
              discountPercentage={displayDiscount}
            />
          </NoPrint>
        )}
        {roomOccupancy && <RoomText>{roomOccupancy}</RoomText>}
        {showTaxesAndFees && <PriceRowTaxesAndFees />}
      </PriceWrapper>
    </CurrencyProvider>
  )
}
export default PriceDetails
